import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import {
	ComponentFormController,
	MessagingService,
	catchErrorTo,
	filterNil,
	filterNull,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BdcWalkService } from 'bdc-walkthrough';
import { SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, combineLatest } from 'rxjs';
import {
	finalize,
	map,
	mapTo,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';

import { MfaEngageDialogComponent } from 'dialog/mfa-engage-dialog/mfa-engage-dialog.component';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { UserMoodFrequency } from 'user/model/user-mood-frequency';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';
import { IUserSettings } from 'user/user-settings';

import { AppService } from '../../../app.service';
import {
	CoacheeSettingsState,
	CoacheeSettingsStore,
} from './coachee-settings.store';

@Injectable()
@UntilDestroy()
export class CoacheeSettingsController extends ComponentFormController<
	CoacheeSettingsState,
	any
> {
	public baseI18 = 'presentation.app.page.settings.coachee.';

	public locale$: Observable<string>;
	public calendarLocale$: Observable<any>;
	public hasMfaEnabled$: Observable<boolean>;
	public isUpdatingMfa$: Observable<boolean>;
	public canDisassociateCoach$: Observable<boolean>;
	public isDisassociatingCoach$: Observable<boolean>;
	public frequencyOptions$: Observable<SelectItem[]>;
	public notificationsFormInvalid$: Observable<boolean>;
	public informationFormInvalid$: Observable<boolean>;
	public hasNoMfaEnabled$: Observable<boolean>;
	public isSavingNotifications$: Observable<boolean>;
	public isSavingInformation$: Observable<boolean>;

	constructor(
		store: CoacheeSettingsStore,
		private _router: Router,
		private _fb: FormBuilder,
		private _dialogService: DialogService,
		private _appService: AppService,
		formsManager: AkitaNgFormsManager,
		private _bdcWalkService: BdcWalkService,
		private _translateService: TranslateService,
		private _messagingService: MessagingService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(
			store,
			formsManager,
			`coachee-settings-${guid()}`,
			_fb.group({
				information: _fb.group({
					firstName: ['', Validators.required],
					lastName: ['', Validators.required],
					livingAddress: [''],
					originAddress: [''],
					birthDate: [null],
					phoneNumber: [''],
					linkedInUrl: [''],
					timeZone: [null],
					cvUrl: [''],
				}),
				notifications: _fb.group({
					moodFrequency: [''],
					notifyInactivity: [true],
					notifyUnfinished: [true],
					notifyNewsletter: [false],
					notifyWeeklyReport: [true],
				}),
			}),
			{ debounceTime: 150 }
		);

		this._initObservables();
		this._initListeners();
	}

	public save(): Observable<void> {
		this.store.update({ isSavingNotifications: true, isSavingInformation: true });

		return this._userApplicationPort.observeSessionUserId().pipe(
			take(1),
			switchMap((userId) =>
				combineLatest([
					this._userApplicationPort.updateUser({
						livingAddress: this.form.value.information.livingAddress ?? '',
						originAddress: this.form.value.information.originAddress ?? '',
						linkedInUrl: this.form.value.information.linkedInUrl ?? '',
						moodFrequency: this.form.value.notifications.moodFrequency,
						phoneNumber: this.form.value.information.phoneNumber,
						firstName: this.form.value.information.firstName,
						birthDate: this.form.value.information.birthDate,
						cvUrl: this.form.value.information.cvUrl ?? '',
						lastName: this.form.value.information.lastName,
						timeZone: this.form.value.information.timeZone,
						id: userId,
					}),
					this._userApplicationPort.updateUserSettings({
						notifyModuleUnfinished: this.form.value.notifications.notifyUnfinished,
						notifyWeeklyReport: this.form.value.notifications.notifyWeeklyReport,
						notifyInactivity: this.form.value.notifications.notifyInactivity,
						id: userId,
					}),
					this._userApplicationPort.updateUserGrantNewsLetter(
						this.form.value.notifications.notifyNewsletter
					),
				])
			),
			tap(() =>
				this._messagingService.publishMessage({
					summaryKey: 'presentation.app.page.settings.coachee.saved.summary',
					detailKey: 'presentation.app.page.settings.coachee.saved.detail',
					severity: 'success',
				})
			),
			finalize(() =>
				this.store.update({
					isSavingNotifications: false,
					isSavingInformation: false,
				})
			),
			catchErrorTo(null),
			mapTo(void 0)
		);
	}

	public disassociateCoach() {
		this.store.update({ isDisassociatingCoach: true });

		this._userApplicationPort
			.observeSessionUserId()
			.pipe(
				filterNil(),
				take(1),
				switchMap((userId) =>
					this._userApplicationPort.disassociateCoachWithCoachee(userId)
				),
				finalize(() => this.store.update({ isDisassociatingCoach: false }))
			)
			.subscribe();
	}

	public triggerProductTour() {
		this._bdcWalkService.reset();
		this._router.navigate(['/']);
	}

	public disableMfa() {
		this.store.update({ isUpdatingMfa: true });

		this._userApplicationPort
			.disableUserMfa()
			.pipe(finalize(() => this.store.update({ isUpdatingMfa: false })))
			.subscribe();
	}

	public enableMfa() {
		this.store.update({ isUpdatingMfa: true });

		this._userApplicationPort
			.generateUserMfa()
			.pipe(
				tap((mfaEngage) =>
					this._dialogService.open(MfaEngageDialogComponent, {
						header: this._translateService.instant(
							'presentation.app.common.dialog.mfa-engage.header'
						),
						closeOnEscape: true,
						data: mfaEngage,
						width: '500px',
						closable: true,
						modal: true,
					})
				),
				finalize(() => this.store.update({ isUpdatingMfa: false }))
			)
			.subscribe();
	}

	private _initListeners() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				tap((coachee) => this._updateInformationForm(coachee)),
				untilDestroyed(this)
			)
			.subscribe();

		combineLatest([
			this._userApplicationPort.fetchUserSettings(),
			this._userApplicationPort.isUserNewsletterEnabled(),
		])
			.pipe(
				tap(([notifications, newsletterEnabled]) =>
					this._updateSettingsForm(notifications, newsletterEnabled)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.locale$ = this._appService.currentLang$;

		this.notificationsFormInvalid$ = this.selectValid('notifications').pipe(
			map((valid) => !valid),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.informationFormInvalid$ = this.selectValid('information').pipe(
			map((valid) => !valid),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSavingNotifications$ = this.select('isSavingNotifications').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSavingInformation$ = this.select('isSavingInformation').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		const sessionUser$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.canDisassociateCoach$ = sessionUser$.pipe(
			switchMap((user) =>
				user.organization$.pipe(
					map(
						(organization) =>
							!!user.coachId &&
							organization.isPropertyTrue(
								OrganizationPropertyName.COACH_DISSOCIATION_ENABLED,
								false
							)
					)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.hasMfaEnabled$ = sessionUser$.pipe(
			map((user) => user.hasMfa),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.hasNoMfaEnabled$ = sessionUser$.pipe(
			map((user) => !user.hasMfa),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.frequencyOptions$ = this._translateService
			.stream('presentation.app.page.settings.coachee.moodFrequency.options')
			.pipe(
				map((frequencies) =>
					Object.keys(UserMoodFrequency).map(
						(frequency) =>
							({
								label: frequencies[frequency],
								value: frequency,
							} as SelectItem)
					)
				),
				shareReplay({ bufferSize: 1, refCount: true })
			);

		this.calendarLocale$ = this._translateService
			.stream('presentation.primeng.calendar')
			.pipe(filterNull(), shareReplay({ bufferSize: 1, refCount: true }));

		this.isDisassociatingCoach$ = this.select('isDisassociatingCoach').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isUpdatingMfa$ = this.select('isUpdatingMfa').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	private _updateInformationForm(coachee: IUser): void {
		this.form.patchValue({
			information: {
				livingAddress: coachee?.metadata.livingAddress ?? '',
				originAddress: coachee?.metadata.originAddress ?? '',
				linkedInUrl: coachee?.metadata.linkedInUrl ?? '',
				birthDate: coachee?.metadata.birthDate,
				cvUrl: coachee?.metadata.cvUrl ?? '',
				phoneNumber: coachee?.phoneNumber,
				firstName: coachee?.firstName,
				lastName: coachee?.lastName,
				timeZone: coachee?.timeZone,
			},
			notifications: {
				moodFrequency: coachee?.metadata.moodFrequency ?? UserMoodFrequency.DAY,
			},
		});
	}

	private _updateSettingsForm(
		notifications: IUserSettings,
		newsletterEnabled: boolean
	): void {
		this.form.patchValue({
			notifications: {
				notifyNewsletter: newsletterEnabled,
				notifyInactivity: notifications?.notifyInactivity,
				notifyUnfinished: notifications?.notifyModuleUnfinished,
				notifyWeeklyReport: notifications?.notifyWeeklyReport,
			},
		});
	}
}
